import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function OskarLogo() {
  return (
    <div style={{ textAlign: "center", marginBottom: 28 }}>
      <Link to="/" style={{ display: "inline-block", lineHeight: 0 }}>
        <img
          src="/oskar_india_tango_logo.png"
          alt="Oskar India Tango"
          style={{ height: 68, width: "auto", display: "inline-block" }}
        />
      </Link>
    </div>
  );
}

const inputStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "9px 12px", fontSize: 14,
  border: "1px solid #d0dcea", borderRadius: 8,
  outline: "none", color: "#1e3a5f", background: "white",
};

const labelStyle = {
  fontSize: 12, fontWeight: 500, color: "#4a6a8a",
  display: "block", marginBottom: 5,
};

const sectionLabelStyle = {
  fontSize: 11, fontWeight: 600, letterSpacing: "0.07em",
  textTransform: "uppercase", color: "#94afc9",
  marginBottom: 12, marginTop: 4,
};

export default function UserRegistration() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const location = useLocation();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirmPassword: "",
    company_name: "",
    company_country: "",
    company_email: "",
    company_chamber_of_commerce: "",
    company_btw_number: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cocMatch, setCocMatch] = useState(null);
  const [cocChecking, setCocChecking] = useState(false);
  const [joiningExisting, setJoiningExisting] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailFromUrl = params.get("email");
    if (emailFromUrl) {
      setFormData((prev) => ({ ...prev, email: emailFromUrl }));
    }
  }, [location.search]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFocus = (e) => { e.target.style.borderColor = "#1e3a5f"; };
  const handleBlur  = (e) => { e.target.style.borderColor = "#d0dcea"; };

  async function checkCompanyByCoc() {
    const coc = formData.company_chamber_of_commerce.trim();
    if (!coc) {
      setCocMatch(null);
      return;
    }
    setCocChecking(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/companies/lookup-by-coc?coc=${encodeURIComponent(coc)}`);
      const data = await res.json().catch(() => null);
      setCocMatch(data?.company || null);
    } catch {
      setCocMatch(null);
    } finally {
      setCocChecking(false);
    }
  }

  function joinExistingCompany() {
    if (!cocMatch) return;
    setJoiningExisting(true);
    setFormData((prev) => ({
      ...prev,
      company_name: cocMatch.name || prev.company_name,
      company_country: cocMatch.country || prev.company_country,
      company_email: cocMatch.email || prev.company_email,
    }));
  }

  function cancelJoinExisting() {
    setJoiningExisting(false);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          password: formData.password,
          company_name: formData.company_name,
          company_country: formData.company_country,
          company_email: formData.company_email,
          company_chamber_of_commerce: formData.company_chamber_of_commerce || null,
          company_btw_number: formData.company_btw_number || null,
          join_existing_company_id: joiningExisting && cocMatch ? cocMatch.id : null,
        }),
      });

      let data = null;
      try { data = await response.json(); } catch { /* ignore */ }

      if (!response.ok) {
        throw new Error(data?.detail || data?.message || `Registration failed (HTTP ${response.status})`);
      }

      login(data);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: "#1e3a5f",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "40px 16px 40px",
    }}>
      <div style={{ width: "100%", maxWidth: 440 }}>
        <OskarLogo />

        <div style={{
          background: "white", borderRadius: 16,
          padding: "32px 28px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
        }}>
          <h1 style={{ fontSize: 18, fontWeight: 500, color: "#1e3a5f", margin: "0 0 4px" }}>
            Create your account
          </h1>
          <p style={{ fontSize: 13, color: "#6b8cae", margin: "0 0 24px" }}>
            14-day free trial · no credit card required
          </p>

          {error && (
            <div style={{
              background: "#fff5f5", border: "1px solid #fed7d7",
              borderRadius: 8, padding: "10px 14px", marginBottom: 16,
              fontSize: 13, color: "#c0392b",
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 0 }}>

            {/* Your details */}
            <div style={sectionLabelStyle}>Your details</div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>First name</label>
                <input
                  type="text" name="first_name" value={formData.first_name}
                  onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur}
                  required style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Last name</label>
                <input
                  type="text" name="last_name" value={formData.last_name}
                  onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur}
                  required style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Email</label>
              <input
                type="email" name="email" value={formData.email}
                onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur}
                required placeholder="you@example.com" autoComplete="username"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Password</label>
              <input
                type="password" name="password" value={formData.password}
                onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur}
                required placeholder="Min. 8 characters" autoComplete="new-password"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>Confirm password</label>
              <input
                type="password" name="confirmPassword" value={formData.confirmPassword}
                onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur}
                required placeholder="••••••••" autoComplete="new-password"
                style={inputStyle}
              />
            </div>

            {/* Company details */}
            <div style={sectionLabelStyle}>Company details</div>

            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Company name</label>
              <input
                type="text" name="company_name" value={formData.company_name}
                onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur}
                disabled={joiningExisting}
                required style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Country</label>
              <input
                type="text" name="company_country" value={formData.company_country}
                onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur}
                disabled={joiningExisting}
                required placeholder="e.g. Netherlands" style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Company email</label>
              <input
                type="email" name="company_email" value={formData.company_email}
                onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur}
                disabled={joiningExisting}
                required style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>
                Chamber of commerce number
                <span style={{ color: "#94afc9", fontWeight: 400, marginLeft: 4 }}>(optional)</span>
              </label>
              <input
                type="text" name="company_chamber_of_commerce"
                value={formData.company_chamber_of_commerce}
                onChange={handleChange} onFocus={handleFocus}
                onBlur={(e) => { handleBlur(e); checkCompanyByCoc(); }}
                disabled={joiningExisting}
                style={inputStyle}
              />
              {cocChecking && (
                <div style={{ fontSize: 12, color: "#6b8cae", marginTop: 6 }}>Checking…</div>
              )}
              {cocMatch && !joiningExisting && (
                <div style={{ marginTop: 8, padding: "10px 12px", background: "#fff8e1", border: "1px solid #f5d98a", borderRadius: 8, fontSize: 13, color: "#5a4a1a" }}>
                  A company with this CoC number already exists: <strong>{cocMatch.name}</strong>{cocMatch.country ? `, ${cocMatch.country}` : ""}.
                  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    <button type="button" onClick={joinExistingCompany}
                      style={{ padding: "5px 12px", background: "#1e3a5f", color: "white", border: "none", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>
                      Join this company
                    </button>
                    <button type="button" onClick={() => setCocMatch(null)}
                      style={{ padding: "5px 12px", background: "white", border: "1px solid #d0dcea", borderRadius: 6, fontSize: 12, cursor: "pointer", color: "#6b8cae" }}>
                      No, this is a different company
                    </button>
                  </div>
                </div>
              )}
              {joiningExisting && (
                <div style={{ marginTop: 8, padding: "10px 12px", background: "#e6f4ea", border: "1px solid #c3e6cb", borderRadius: 8, fontSize: 13, color: "#2e7d32" }}>
                  ✓ You'll join <strong>{cocMatch?.name}</strong> as a member.{" "}
                  <button type="button" onClick={cancelJoinExisting}
                    style={{ fontSize: 12, color: "#1e7fc7", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                    Undo
                  </button>
                </div>
              )}
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>
                BTW number
                <span style={{ color: "#94afc9", fontWeight: 400, marginLeft: 4 }}>(optional)</span>
              </label>
              <input
                type="text" name="company_btw_number"
                value={formData.company_btw_number}
                onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur}
                disabled={joiningExisting}
                placeholder="e.g. NL123456789B01"
                style={inputStyle}
              />
            </div>

            {/* Terms notice */}
            <p style={{ fontSize: 12, color: "#94afc9", marginBottom: 16, lineHeight: 1.6 }}>
              By creating an account you agree to our{" "}
              <a href="/terms" target="_blank" rel="noreferrer" style={{ color: "#6b8cae" }}>Terms and Conditions</a>
              {" "}and{" "}
              <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: "#6b8cae" }}>Privacy Policy</a>.
            </p>

            <button
              type="submit" disabled={loading}
              style={{
                padding: "11px 0",
                background: loading ? "#6b8cae" : "#1e3a5f",
                color: "white", border: "none", borderRadius: 8,
                fontSize: 14, fontWeight: 500,
                cursor: loading ? "not-allowed" : "pointer",
                transition: "background 0.15s",
              }}
            >
              {loading ? "Creating account…" : "Create account"}
            </button>
          </form>

          <div style={{ textAlign: "center", marginTop: 20 }}>
            <span style={{ fontSize: 13, color: "#94afc9" }}>Already have an account? </span>
            <Link to="/login" style={{ fontSize: 13, color: "#1e3a5f", fontWeight: 500, textDecoration: "underline" }}>
              Sign in
            </Link>
          </div>

          <div style={{ textAlign: "center", marginTop: 12 }}>
            <Link to="/" style={{ fontSize: 12, color: "#b0c4d8", textDecoration: "none" }}>
              ← Back to homepage
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}